import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { MailTemplateModel } from "../../api/mailTemplates/MailTemplateModel";
import { MailTemplateAdapter } from "../../api/mailTemplates/MailTemplateAdapter";
import type {
  CommunicationSettings,
  MailPlaceholderDefinition,
  MailTemplate,
  MailTemplateType,
} from "../../api/mailTemplates/MailTemplate.typed";

interface MailTemplateContextType {
  listMailTemplates: (type?: MailTemplateType) => Promise<MailTemplate[] | undefined>;
  getMailTemplate: (id: number) => Promise<MailTemplate | undefined>;
  createMailTemplate: (payload: MailTemplate) => Promise<MailTemplate | undefined>;
  updateMailTemplate: (id: number, payload: MailTemplate) => Promise<boolean>;
  deleteMailTemplate: (id: number) => Promise<void>;
  listMailPlaceholderDefinitions: (
    templateType: MailTemplateType,
  ) => Promise<MailPlaceholderDefinition[] | undefined>;
  previewMailTemplateHtml: (
    bodyHtml: string,
    type: MailTemplateType,
  ) => Promise<string | undefined>;
  getCommunicationSettings: () => Promise<CommunicationSettings | undefined>;
  updateCommunicationSettings: (payload: CommunicationSettings) => Promise<boolean>;
}

const MailTemplateContext = React.createContext<MailTemplateContextType>(null!);

export function MailTemplateProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new MailTemplateModel(appConfig);
    return new MailTemplateAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const listMailTemplates = React.useCallback(
    async (type?: MailTemplateType): Promise<MailTemplate[] | undefined> =>
      adapter ? wrap(() => adapter.listMailTemplates(type)) : undefined,
    [adapter, wrap],
  );

  const getMailTemplate = React.useCallback(
    async (id: number): Promise<MailTemplate | undefined> =>
      adapter ? wrap(() => adapter.getMailTemplate(id)) : undefined,
    [adapter, wrap],
  );

  const createMailTemplate = React.useCallback(
    async (payload: MailTemplate): Promise<MailTemplate | undefined> =>
      adapter ? wrap(() => adapter.createMailTemplate(payload)) : undefined,
    [adapter, wrap],
  );

  const updateMailTemplate = React.useCallback(
    async (id: number, payload: MailTemplate): Promise<boolean> => {
      if (!adapter) return false;
      try {
        await adapter.updateMailTemplate(id, payload);

        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");

        return false;
      }
    },
    [adapter, showAlert],
  );

  const deleteMailTemplate = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteMailTemplate(id);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const listMailPlaceholderDefinitions = React.useCallback(
    async (templateType: MailTemplateType): Promise<MailPlaceholderDefinition[] | undefined> =>
      adapter ? wrap(() => adapter.listMailPlaceholderDefinitions(templateType)) : undefined,
    [adapter, wrap],
  );

  const previewMailTemplateHtml = React.useCallback(
    async (bodyHtml: string, type: MailTemplateType): Promise<string | undefined> =>
      adapter ? wrap(() => adapter.previewMailTemplateHtml(bodyHtml, type)) : undefined,
    [adapter, wrap],
  );

  const getCommunicationSettings = React.useCallback(
    async (): Promise<CommunicationSettings | undefined> =>
      adapter ? wrap(() => adapter.getCommunicationSettings()) : undefined,
    [adapter, wrap],
  );

  const updateCommunicationSettings = React.useCallback(
    async (payload: CommunicationSettings): Promise<boolean> => {
      if (!adapter) return false;
      try {
        await adapter.updateCommunicationSettings(payload);

        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");

        return false;
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo<MailTemplateContextType>(
    () => ({
      listMailTemplates,
      getMailTemplate,
      createMailTemplate,
      updateMailTemplate,
      deleteMailTemplate,
      listMailPlaceholderDefinitions,
      previewMailTemplateHtml,
      getCommunicationSettings,
      updateCommunicationSettings,
    }),
    [
      listMailTemplates,
      getMailTemplate,
      createMailTemplate,
      updateMailTemplate,
      deleteMailTemplate,
      listMailPlaceholderDefinitions,
      previewMailTemplateHtml,
      getCommunicationSettings,
      updateCommunicationSettings,
    ],
  );

  return (
    <MailTemplateContext.Provider value={value}>{props.children}</MailTemplateContext.Provider>
  );
}

export function useMailTemplate(): MailTemplateContextType {
  const ctx = React.useContext(MailTemplateContext);
  if (!ctx) throw new Error("useMailTemplate must be used within MailTemplateProvider");
  return ctx;
}
